/**
 * LCDP-14855 - see commit description for details
 */

import React, { useState, useEffect, useCallback } from 'react';
import Scrollbars from '@atomic-reactor/react-custom-scrollbars';
import clsx from 'clsx';

/**
 * @param props.id
 * @param props.label
 * @param props.name
 * @param props.defaultValue
 * @param props.options
 * @param props.onChange
 * @param props.inputRef
 */

const DoubleColumnSelect = (props): React.JSX.Element => {
    const [isOpen, setIsOpen] = useState(false);
    const selectRef = React.useRef(null);

    const toggleSelect = () => {
        setIsOpen(!isOpen);
    };

    const handleKeyDown = event => {
        if (event.key == 'Enter') {
            toggleSelect();
        }

        if (event.key == 'Escape') {
            setIsOpen(false);
        }
    };

    const handleExitClick = event => {
        if (selectRef.current && !selectRef.current.contains(event.target)) {
            setIsOpen(false);
        }
    };

    useEffect(() => {
        document.addEventListener('click', handleExitClick, true);

        return () => {
            document.removeEventListener('click', handleExitClick, true);
        };
    }, []);

    useEffect(() => {
        if (props.reset && props.onChange) {
            props.onChange(props.defaultValue);
        }
    }, [props.reset]);

    const onChange = useCallback(value => {
        setIsOpen(false);
        if (props.onChange) props.onChange(formatValue(value));
    }, []);

    const onKeyPress = useCallback((e, value) => {
        if (e === 'Enter') {
            setIsOpen(false);
            props.onChange(formatValue(value));
        }
    }, []);

    const renderOptions = options => {
        const renderOption = (option, className) => {
            const optionValue =
                option.props.value > 0 && !option.props.children.startsWith('+')
                    ? `+${option.props.children}`
                    : option.props.children;
            return (
                <div
                    key={option.props.value}
                    className={className}
                    onClick={() => onChange(option.props.value)}
                    onKeyDown={e => onKeyPress(e.key, option.props.value)}
                    tabIndex={props.disabled ? -1 : 0}
                >
                    <div
                        className={clsx('option-value', {
                            'option-value-selected': optionValue.toString() == fieldValue(),
                        })}
                    >
                        {optionValue}
                    </div>
                </div>
            );
        };

        // const zeroOption = options.filter(option => {
        //     return option.props.value === 0 ? option : null;
        // });
        const negativeOptions = options
            .filter(option => {
                return option.props.value < 0 ? option : null;
            })
            .sort((a, b) => {
                return a.props.value > b.props.value ? -1 : 1;
            });
        const positiveOptions = options
            .filter(option => {
                return option.props.value >= 0 ? option : null;
            })
            .sort((a, b) => {
                return a.props.value < b.props.value ? -1 : 1;
            });

        return (
            <>
                <div className="header-select">
                    <div>-</div>
                    <div>+</div>
                </div>
                <Scrollbars
                    onClick={e => e.stopPropagation()}
                    renderThumbVertical={props => (
                        <div {...props} className="cl-prescription-select-thumb"></div>
                    )}
                    renderTrackVertical={props => (
                        <div {...props} className="cl-prescription-select-track"></div>
                    )}
                    renderView={props => <div {...props} className="cl-prescription-select-view" />}
                >
                    {/* {zeroOption.length > 0
                        ? renderOption(
                              zeroOption[0],
                              'cl-prescription-select-table-row cl-prescription-select-table-option'
                          )
                        : null} */}
                    {negativeOptions.length > 0 && positiveOptions.length > 0 ? (
                        <div
                            className="cl-prescription-select-table-row scrollable-menu"
                            style={{ overflow: 'visible' }}
                        >
                            <div className="cl-prescription-select-table-col">
                                {negativeOptions.map(negativeOption => {
                                    return renderOption(
                                        negativeOption,
                                        'cl-prescription-select-table-row cl-prescription-select-table-option'
                                    );
                                })}
                            </div>
                            <div className="cl-prescription-select-table-col">
                                {positiveOptions.map(positiveOption => {
                                    return renderOption(
                                        positiveOption,
                                        'cl-prescription-select-table-row cl-prescription-select-table-option'
                                    );
                                })}
                            </div>
                        </div>
                    ) : null}
                </Scrollbars>
            </>
        );
    };

    const fieldValue = () => {
        let value = '';

        if (props.value === undefined || props.value === null || props.value === '') {
            value = '';
        } else {
            value = formatValue(Number(props.value));
        }
        return value;
    };

    const formatValue = (val: number) => {
        const isIntegerValue = Number.isInteger(val);
        let formatVal = '';
        //0 has to be '+0.00'
        if (val >= 0) {
            formatVal = isIntegerValue ? `+${val}.00` : `+${val.toFixed(2)}`;
        } else {
            formatVal = isIntegerValue ? `${val}.00` : `${val.toFixed(2)}`;
        }

        return formatVal;
    };

    return (
        <div
            className={
                'PrescriptionSelect cl-prescription-select' +
                (props.disabled ? ' cl-prescription-select-disabled' : '') +
                (isOpen ? ' cl-prescription-select-open' : '')
            }
            onClick={props.disabled ? () => {} : toggleSelect}
            onKeyDown={props.disabled ? () => {} : handleKeyDown}
            ref={selectRef}
            tabIndex={props.disabled ? -1 : 0}
        >
            <input
                id={props.id}
                aria-label={props.label}
                className="cl-prescription-select-value"
                value={fieldValue()}
                name={props.name}
                tabIndex={-1}
                readOnly
                ref={props.inputRef}
                {...props.register}
            />
            <svg viewBox="0 0 27 24" fill="none" className="cl-prescription-select-arrow">
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M4.982 8.293a.928.928 0 000 1.414l7.82 7c.437.39 1.144.39 1.58 0l7.821-7a.928.928 0 000-1.414 1.212 1.212 0 00-1.58 0l-7.03 6.293-7.031-6.293a1.212 1.212 0 00-1.58 0z"
                ></path>
            </svg>
            {isOpen ? (
                <div className="cl-prescription-select-table">{renderOptions(props.options)}</div>
            ) : null}
        </div>
    );
};

export default DoubleColumnSelect;
